import { useQuery } from "@tanstack/react-query"
import type {
  UseBaseQueryOptions,
  UseQueryResult,
  QueryKey,
  WithRequired,
} from "@tanstack/react-query"

import client from "~/backend/axiosClient"

import type {
  FindIngestionsQueryResponse,
  FindIngestionsQueryParams,
} from "../types/FindIngestions"

type FindIngestionsClient = typeof client<
  FindIngestionsQueryResponse,
  Error,
  never
>
type FindIngestions = {
  data: FindIngestionsQueryResponse
  error: Error
  request: never
  pathParams: never
  queryParams: FindIngestionsQueryParams
  headerParams: never
  response: Awaited<ReturnType<FindIngestionsClient>>
  client: {
    parameters: Partial<Parameters<FindIngestionsClient>[0]>
    return: Awaited<ReturnType<FindIngestionsClient>>
  }
}
export const findIngestionsQueryKey = (
  params?: FindIngestions["queryParams"]
) => [{ url: "/v1/ingestions" }, ...(params ? [params] : [])] as const
export type FindIngestionsQueryKey = ReturnType<typeof findIngestionsQueryKey>
export function findIngestionsQueryOptions<
  TData = FindIngestions["response"],
  TQueryData = FindIngestions["response"],
>(
  params?: FindIngestions["queryParams"],
  options: FindIngestions["client"]["parameters"] = {}
): WithRequired<
  UseBaseQueryOptions<
    FindIngestions["response"],
    FindIngestions["error"],
    TData,
    TQueryData
  >,
  "queryKey"
> {
  const queryKey = findIngestionsQueryKey(params)
  return {
    queryKey,
    queryFn: async () => {
      const res = await client<FindIngestions["data"], FindIngestions["error"]>(
        {
          method: "get",
          url: `/v1/ingestions`,
          params,
          ...options,
        }
      )
      return res
    },
  }
}
/**
 * @link /v1/ingestions
 */
export function useFindIngestions<
  TData = FindIngestions["response"],
  TQueryData = FindIngestions["response"],
  TQueryKey extends QueryKey = FindIngestionsQueryKey,
>(
  params?: FindIngestions["queryParams"],
  options: {
    query?: Partial<
      UseBaseQueryOptions<
        FindIngestions["response"],
        FindIngestions["error"],
        TData,
        TQueryData,
        TQueryKey
      >
    >
    client?: FindIngestions["client"]["parameters"]
  } = {}
): UseQueryResult<TData, FindIngestions["error"]> & {
  queryKey: TQueryKey
} {
  const { query: queryOptions, client: clientOptions = {} } = options ?? {}
  const queryKey = queryOptions?.queryKey ?? findIngestionsQueryKey(params)
  const query = useQuery<
    FindIngestions["data"],
    FindIngestions["error"],
    TData,
    any
  >({
    ...findIngestionsQueryOptions<TData, TQueryData>(params, clientOptions),
    queryKey,
    ...queryOptions,
  }) as UseQueryResult<TData, FindIngestions["error"]> & {
    queryKey: TQueryKey
  }
  query.queryKey = queryKey as TQueryKey
  return query
}
